import { graphqlQuery } from "@/lib/api/graphql";
import type { ApiConfig } from "@/providers/ApiConfigProvider";
import type { Transaction } from "@/types/transaction";
import type { CustomerAgeingUI } from "@/types/ui/customer-detail";
import type { CustomerAgeingApi } from "@/types/api/customer-detail";
import type { LogoSource } from "@/types/template-builder";
import type { AgeingBandConfig } from "@/types/ageing-analysis";
import { isStubResponse } from "@/types/api/customer-detail";
import { mapCustomerAgeingApiToUi } from "@/lib/mappers/customer-detail";
import { fetchImageAsDataUrl } from "@/hooks/useImageToDataUrl";
import {
  TRANSACTION_LIST_QUERY,
  type TransactionListGraphQLResponse,
  mapGraphQLNodeToTransaction,
} from "@/hooks/useTransactionsGraphQL";

export interface CustomerTransactionData {
  outstanding: Transaction[];
  overdue: Transaction[];
}

/**
 * Fetches all outstanding transactions for a single customer via GraphQL.
 * Used by both statement PDF attachment generation and statement preview.
 */
export async function fetchOutstandingTransactions(
  apiConfig: Pick<ApiConfig, "apiBaseUrl" | "getHeaders">,
  customerId: string,
  excludeDisputed?: boolean,
): Promise<Transaction[]> {
  const customerIdNum = Number(customerId);
  const filter: Record<string, unknown> = {
    customerId: { eq: customerIdNum },
    outstandingAmount: { ne: 0 },
    transactionTypeCode: { eq: "invoice" },
  };
  if (excludeDisputed) {
    filter.hasDispute = { eq: false };
    filter.onDispute = { eq: false };
  }

  const response = await graphqlQuery<TransactionListGraphQLResponse>(
    apiConfig,
    TRANSACTION_LIST_QUERY,
    { first: 0, filter },
  );

  return response.transactions.transactionList.edges.map((e) => mapGraphQLNodeToTransaction(e.node));
}

/**
 * Fetches customer ageing data via REST API.
 * Used by both statement PDF attachment generation and statement preview.
 */
export async function fetchCustomerAgeing(
  apiConfig: Pick<ApiConfig, "apiBaseUrl" | "getHeaders">,
  customerId: string,
  currency?: string,
): Promise<CustomerAgeingUI | null> {
  const headers = await apiConfig.getHeaders();
  const qs = currency ? `?currency=${encodeURIComponent(currency)}` : "";
  const res = await fetch(`${apiConfig.apiBaseUrl}/api/v1/customers/${customerId}/aging${qs}`, {
    headers: headers as Record<string, string>,
  });

  if (!res.ok) return null;

  const json = (await res.json()) as CustomerAgeingApi;
  if (isStubResponse(json)) return null;

  const bands = await fetchAgeingBandsConfig(apiConfig);
  return mapCustomerAgeingApiToUi(json, bands);
}

export async function fetchAgeingBandsConfig(
  apiConfig: Pick<ApiConfig, "apiBaseUrl" | "getHeaders">,
): Promise<AgeingBandConfig[]> {
  const DEFAULT_BANDS: AgeingBandConfig[] = [
    { id: 1, sequence: 1, startDays: 0, endDays: 30, label: "Current", bandLabel: "0-30 Days", color: "#22c55e" },
    { id: 2, sequence: 2, startDays: 31, endDays: 60, label: "31-60 Days", bandLabel: "31-60 Days", color: "#eab308" },
    { id: 3, sequence: 3, startDays: 61, endDays: 90, label: "61-90 Days", bandLabel: "61-90 Days", color: "#f97316" },
    { id: 4, sequence: 4, startDays: 91, endDays: 120, label: "91-120 Days", bandLabel: "91-120 Days", color: "#ef4444" },
    { id: 5, sequence: 5, startDays: 121, endDays: null, label: "Over 120 Days", bandLabel: "121+ Days", color: "#dc2626" },
  ];
  try {
    const headers = await apiConfig.getHeaders();
    const res = await fetch(`${apiConfig.apiBaseUrl}/api/v1/settings/aging-bands`, {
      headers: headers as Record<string, string>,
    });
    if (!res.ok) return DEFAULT_BANDS;
    const data: Array<{ id: number; sequence: number; startDays: number; endDays: number | null; label: string; bandLabel: string; isActive: boolean; color: string }> = await res.json();
    return data
      .filter((x) => x.isActive)
      .sort((a, b) => a.sequence - b.sequence)
      .map((x) => ({ id: x.id, sequence: x.sequence, startDays: x.startDays, endDays: x.endDays, label: x.label, bandLabel: x.bandLabel, color: x.color || "#6b7280" }));
  } catch {
    return DEFAULT_BANDS;
  }
}

export interface ResolveLogoParams {
  pdfSettings: { logoSource?: LogoSource; logo?: { url?: string }; logoSize?: string };
  companyLogoUrl: string | null;
  apiBaseUrl: string;
  companyId: string | undefined;
  getHeaders: () => Promise<HeadersInit>;
}

/**
 * Resolves the statement logo URL to a data URL.
 * Shared by both PDF attachment generation and statement preview.
 */
export async function resolveStatementLogoUrl(
  params: ResolveLogoParams,
): Promise<{ logoUrl: string | null; logoSource: LogoSource }> {
  const { pdfSettings: ps, companyLogoUrl, apiBaseUrl, companyId, getHeaders } = params;
  const logoSource: LogoSource = ps.logoSource ?? (ps.logo?.url ? 'custom' : (companyLogoUrl ? 'company' : 'none'));
  const rawLogoUrl = logoSource === 'company' && companyId
    ? `${apiBaseUrl}/api/v1/company/${companyId}/logo`
    : logoSource === 'custom'
      ? (ps.logo?.url ?? null)
      : null;
  const headers = await getHeaders();
  const logoUrl = await fetchImageAsDataUrl(rawLogoUrl, { headers: headers as Record<string, string> });
  return { logoUrl, logoSource };
}

/**
 * Fetches outstanding transactions for multiple customers in a single GraphQL call,
 * then groups and categorises them per customer.
 */
/**
 * Fetches the latest receipt for a customer via GraphQL.
 * Returns the amount fields from the most recent receipt, or null if none found.
 */
export async function fetchLatestReceipt(
  apiConfig: Pick<ApiConfig, "apiBaseUrl" | "getHeaders">,
  customerId: string,
): Promise<{ amount: number; currencyAmount: number | null; currencyCode: string | null } | null> {
  const query = `
    query LatestReceipt($first: Int, $filter: ReceiptListFilterInput, $sort: [ReceiptListSortSortByInput!]) {
      receipts {
        receiptList(first: $first, filter: $filter, sort: $sort) {
          edges {
            node {
              amount
              currencyAmount
              currencyCode
            }
          }
        }
      }
    }
  `;

  const response = await graphqlQuery<{
    receipts: {
      receiptList: {
        edges: Array<{
          node: {
            amount: number;
            currencyAmount: number | null;
            currencyCode: string | null;
          };
        }>;
      };
    };
  }>(apiConfig, query, {
    first: 1,
    filter: { customerId: { eq: Number(customerId) } },
    sort: [{ field: "TransactionDate", order: "Descending" }, { field: "TransactionId", order: "Descending" }],
  });

  const edges = response.receipts.receiptList.edges;
  if (edges.length === 0) return null;

  const node = edges[0].node;
  return {
    amount: node.amount,
    currencyAmount: node.currencyAmount,
    currencyCode: node.currencyCode,
  };
}

/**
 * Fetches outstanding transactions for multiple customers in a single GraphQL call,
 * then groups and categorises them per customer.
 */
export async function fetchTransactionsForCustomers(
  apiConfig: Pick<ApiConfig, "apiBaseUrl" | "getHeaders">,
  customerIds: string[],
  excludeDisputed?: boolean,
): Promise<Map<string, CustomerTransactionData>> {
  if (customerIds.length === 0) return new Map();

  const numericIds = customerIds.map(Number);

  // Fetch all transactions with outstandingAmount != 0 for the given customers
  const filter: Record<string, unknown> = {
    customerId:
      numericIds.length === 1
        ? { eq: numericIds[0] }
        : { containsValues: numericIds },
    outstandingAmount: { ne: 0 },
    transactionTypeCode: { eq: "invoice" },
  };
  if (excludeDisputed) {
    filter.hasDispute = { eq: false };
    filter.onDispute = { eq: false };
  }

  // Paginate through all matching transactions
  let allTransactions: Transaction[] = [];
  let after: string | null = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const variables: Record<string, unknown> = {
      first: 1000,
      filter,
      ...(after ? { after } : {}),
    };

    const response = await graphqlQuery<TransactionListGraphQLResponse>(
      apiConfig,
      TRANSACTION_LIST_QUERY,
      variables,
    );

    const edges = response.transactions.transactionList.edges;
    const mapped = edges.map((e) => mapGraphQLNodeToTransaction(e.node));
    allTransactions = allTransactions.concat(mapped);

    const pageInfo = response.transactions.transactionList.pageInfo;
    hasNextPage = pageInfo.hasNextPage;
    after = pageInfo.endCursor;
  }

  // Group by customerId
  const result = new Map<string, CustomerTransactionData>();
  for (const id of customerIds) {
    result.set(id, { outstanding: [], overdue: [] });
  }

  for (const tx of allTransactions) {
    const key = String(tx.customerId);
    const bucket = result.get(key);
    if (!bucket) continue;
    bucket.outstanding.push(tx);
    if (tx.status === "overdue") {
      bucket.overdue.push(tx);
    }
  }

  return result;
}
